<template>
  <div
    class="chat-root"
    :style="`--primary:${$store.state.theme.primaryColor};--btn:${$store.state.theme.buttonColor}`"
  >
    <!-- SIDEBAR -->
    <div class="chat-sidebar">
      <div class="sidebar-header">
        <span class="sidebar-title">Messages</span>
        <div class="sh-actions">
          <button
            class="icon-btn sync-btn"
            :class="{ spin: syncing }"
            title="Sync Groups"
            @click="syncGroups"
          >
            <v-icon size="18">mdi-sync</v-icon>
          </button>
          <!-- <span class="conn-dot" :class="isConnected ? 'green' : 'red'"></span> -->
        </div>
      </div>
      <div class="sidebar-list">
        <div v-if="loadingConvs" class="loading-state">Loading groups…</div>
        <template v-else>
          <div
            v-for="c in conversations"
            :key="c.id"
            class="conv-item"
            :class="{
              active: selected && selected.id === c.id,
              unread: c.unread_count > 0,
            }"
            @click="selectConv(c)"
          >
            <div
              class="conv-avatar"
              :style="`background:${groupColor(c.type)}`"
            >
              <v-icon small color="white">{{ groupIcon(c.type) }}</v-icon>
            </div>
            <div class="conv-meta">
              <div class="cm-top">
                <div class="conv-name">{{ c.name }}</div>
                <div v-if="c.unread_count > 0" class="unread-badge">
                  {{ c.unread_count }}
                </div>
              </div>
              <div class="cm-bottom">
                <div class="conv-last" v-if="c.last_message">
                  <template v-if="c.last_message.is_deleted || c.last_message.content === 'This message was deleted'">
                    <v-icon x-small style="margin-right: 2px; opacity: 0.7"
                      >mdi-cancel</v-icon
                    >
                    <i style="opacity: 0.7">This message was deleted</i>
                  </template>
                  <template v-else>
                    <v-icon
                      x-small
                      v-if="c.last_message.message_type !== 'text'"
                      style="margin-right: 2px"
                      >mdi-paperclip</v-icon
                    >
                    {{
                      c.last_message.message_type !== "text"
                        ? c.last_message.message_type
                        : (c.last_message.content || "").substring(0, 40)
                    }}
                  </template>
                </div>
                <div class="conv-sub" v-else>{{ groupLabel(c.type) }}</div>
              </div>
            </div>
          </div>
          <div v-if="!conversations.length" class="empty-state">
            No groups found
          </div>
        </template>
      </div>
    </div>

    <!-- MAIN PANE -->
    <div class="chat-main">
      <!-- Empty State -->
      <div v-if="!selected" class="no-conv">
        <v-icon size="64" color="#6366f1">mdi-chat-processing-outline</v-icon>
        <p>Select a group to start chatting</p>
      </div>

      <template v-else>
        <!-- Header -->
        <div class="chat-header">
          <div class="ch-left">
            <div
              class="ch-avatar"
              :style="`background:${groupColor(selected.type)}`"
            >
              <v-icon small color="white">{{
                groupIcon(selected.type)
              }}</v-icon>
            </div>
            <div>
              <div class="ch-name">{{ selected.name }}</div>
              <div class="ch-sub" @click="drawerOpen = true">
                View Members ({{
                  loadingMembers ? (selected.total_members || '…') : groupMembers.length
                }}) ›
              </div>
            </div>
          </div>
          <!-- <div class="ch-right">
            <span
              class="status-pill"
              :class="isConnected ? 'online' : 'offline'"
            >
              {{ isConnected ? "Online" : "Offline" }}
            </span>
          </div> -->
        </div>

        <!-- Messages -->
        <div class="chat-messages" ref="msgBox">
          <div v-if="loadingMsgs" class="loading-state">Loading messages…</div>
          <template v-else>
            <div
              v-for="m in messages"
              :key="m.id"
              class="msg-wrap"
              :class="{ mine: isMine(m) }"
            >
              <!-- Avatar for received messages -->
              <div v-if="!isMine(m)" class="msg-avatar">
                <img
                  v-if="m.sender && m.sender.profile_url && !m.imageError"
                  :src="resolveMedia(m.sender.profile_url)"
                  class="avatar-img"
                  @error="$set(m, 'imageError', true)"
                />
                <span v-else>{{
                  getInitials(
                    m.sender_name ||
                      (m.sender && m.sender.name) ||
                      m.sender_type ||
                      "M",
                  )
                }}</span>
              </div>
              <div class="msg-bubble" :class="{ mine: isMine(m) }">
                <div v-if="!isMine(m)" class="msg-sender">
                  {{
                    m.sender_name ||
                    (m.sender && m.sender.name) ||
                    (m.sender_type === "admin" ? "Admin" : "Member")
                  }}
                </div>

                <template v-if="m.is_deleted || m.content === 'This message was deleted'">
                  <div
                    class="msg-deleted"
                    style="font-style: italic; opacity: 0.8"
                  >
                    <v-icon small color="grey" class="mr-1">mdi-cancel</v-icon>
                    This message was deleted
                  </div>
                </template>
                <template v-else>
                  <img
                    v-if="m.message_type === 'image'"
                    :src="resolveMedia(m.content)"
                    class="media-img"
                    @click="openLightbox('image', resolveMedia(m.content))"
                    @load="scrollBottom"
                  />
                  <video
                    v-else-if="m.message_type === 'video'"
                    :src="resolveMedia(m.content)"
                    controls
                    class="media-video"
                    @loadeddata="scrollBottom"
                    @click.prevent="
                      openLightbox('video', resolveMedia(m.content))
                    "
                    style="cursor: pointer"
                  />
                  <audio
                    v-else-if="m.message_type === 'audio'"
                    :src="resolveMedia(m.content)"
                    controls
                    class="media-audio"
                  />
                  <div
                    v-else-if="m.message_type === 'file' && isPdf(m.content)"
                    class="file-attachment pdf-clickable"
                    @click="openLightbox('pdf', resolveMedia(m.content))"
                  >
                    <v-icon small>mdi-file-pdf-box</v-icon>
                    {{ (m.content || "").split("/").pop() || "View PDF" }}
                  </div>
                  <a
                    v-else-if="m.message_type === 'file'"
                    :href="resolveMedia(m.content)"
                    target="_blank"
                    class="file-attachment"
                  >
                    <v-icon small>mdi-file-document-outline</v-icon>
                    {{ (m.content || "").split("/").pop() || "View File" }}
                  </a>
                  <span v-else class="msg-text">{{ m.content }}</span>
                </template>
                <div class="msg-time">
                  {{ formatTime(m.created_at) }}
                  <span
                    v-if="m.is_edited && !m.is_deleted"
                    style="font-size: 9px; margin-left: 4px; opacity: 0.7"
                    >(edited)</span
                  >
                </div>
              </div>

              <!-- Message Actions -->
              <div
                v-if="!m.is_deleted && m.content !== 'This message was deleted'"
                class="msg-actions"
                style="align-self: center"
              >
                <v-menu offset-y left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      class="ml-1"
                      style="opacity: 0.5"
                    >
                      <v-icon x-small>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-if="m.message_type === 'text'"
                      @click="startEdit(m)"
                    >
                      <v-list-item-title
                        ><v-icon small class="mr-2">mdi-pencil</v-icon
                        >Edit</v-list-item-title
                      >
                    </v-list-item>
                    <v-list-item @click="deleteMsg(m)">
                      <v-list-item-title class="red--text"
                        ><v-icon small color="red" class="mr-2"
                          >mdi-delete</v-icon
                        >Delete</v-list-item-title
                      >
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </div>
            <div v-if="isTyping" class="typing-indicator">
              <span></span><span></span><span></span>
              <div style="font-size: 12px; color: #94a3b8; margin-left: 4px">
                {{ typingUserName }} is typing...
              </div>
            </div>
          </template>
        </div>

        <!-- Composer -->
        <div class="composer">
          <div v-if="editingMsg" class="file-preview">
            <v-icon small>mdi-pencil</v-icon> Editing message...
            <button @click="cancelEdit" class="rm-btn">✕</button>
          </div>
          <div v-if="pendingFile && !editingMsg" class="file-preview">
            <v-icon small>mdi-paperclip</v-icon>
            {{ pendingFile.name }} ({{
              (pendingFile.size / 1048576).toFixed(1)
            }}MB)
            <button @click="clearPendingFile" class="rm-btn">✕</button>
          </div>
          <div class="composer-row">
            <input type="file" ref="fi" class="hidden" @change="onFile" />
            <button
              class="icon-btn"
              @click="openFilePicker"
              :disabled="editingMsg || selectingFile"
              title="Attach"
            >
              <v-icon :color="editingMsg ? 'grey' : ''">mdi-attachment</v-icon>
            </button>
            <textarea
              v-model="draft"
              rows="1"
              :placeholder="composerPlaceholder"
              class="msg-input"
              :class="{ 'msg-input--disabled': messageInputDisabled }"
              :disabled="messageInputDisabled"
              @keydown.enter.exact.prevent="send"
              @input="onTyping"
            ></textarea>
            <button
              class="send-btn"
              :disabled="(!draft.trim() && !pendingFile) || sending"
              @click="send"
            >
              <v-icon v-if="!sending" color="white">mdi-send</v-icon>
              <v-icon v-else class="spin" color="white">mdi-loading</v-icon>
            </button>
          </div>
        </div>
      </template>

      <!-- Member Drawer -->
      <transition name="drawer">
        <div
          v-if="drawerOpen"
          class="drawer-overlay"
          @click.self="drawerOpen = false"
        >
          <div class="member-drawer">
            <div class="drawer-header">
              <span>Group Members</span>
              <button @click="drawerOpen = false" class="icon-btn">
                <v-icon>mdi-close</v-icon>
              </button>
            </div>
            <div class="drawer-body">
              <div v-if="loadingMembers" class="loading-state">
                <v-icon class="spin">mdi-loading</v-icon> Loading members…
              </div>
              <div v-else-if="!groupMembers.length" class="empty-state">
                No members found
              </div>
              <div v-else class="member-list">
                <div v-for="m in groupMembers" :key="m.id" class="member-item">
                  <!-- Profile pic or initial avatar -->
                  <div
                    class="member-avatar"
                    :class="m.type === 'admin' ? 'admin' : 'resident'"
                  >
                    <img
                      v-if="m.profile_url && !m.imageError"
                      :src="resolveMedia(m.profile_url)"
                      class="avatar-img"
                      :alt="m.name"
                      @error="$set(m, 'imageError', true)"
                    />
                    <span v-else class="avatar-initial">
                      {{ getInitials(m.name || "?") }}
                    </span>
                  </div>
                  <div class="member-info">
                    <div class="member-name">
                      {{ m.name }}
                      <v-chip
                        v-if="m.type === 'admin'"
                        x-small
                        color="var(--primary)"
                        dark
                        class="ml-2"
                        >Admin</v-chip
                      >
                    </div>
                    <div class="member-role">
                      {{ formatMemberRole(m) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Confirm Deletion -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Message"
      message="Are you sure you want to delete this message?"
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="confirmDeleteMsg"
    />

    <!-- ── Media Viewer (same as events / notices) ── -->
    <MediaViewer
      v-model="mediaViewerOpen"
      :items="mediaViewerItems"
      :index="mediaViewerIndex"
    />
  </div>
</template>

<script>
import io from "socket.io-client";
import { mapMutations } from "vuex";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";

const HARMFUL_CHAT_EXTENSIONS = [
  ".exe",
  ".bat",
  ".cmd",
  ".sh",
  ".php",
  ".js",
  ".vbs",
  ".msi",
  ".scr",
  ".pif",
];

function isHarmfulChatFileName(name) {
  if (!name) return false;
  const lower = String(name).toLowerCase();
  const dot = lower.lastIndexOf(".");
  if (dot === -1) return false;
  return HARMFUL_CHAT_EXTENSIONS.includes(lower.slice(dot));
}

export default {
  name: "SocietyAdminChat",
  components: {
    ConfirmModal,
    MediaViewer,
  },
  data: () => ({
    socket: null,
    isConnected: false,
    conversations: [],
    selected: null,
    messages: [],
    draft: "",
    pendingFile: null,
    selectingFile: false,
    loadingConvs: true,
    loadingMsgs: false,
    sending: false,
    syncing: false,
    drawerOpen: false,
    isTyping: false,
    amITyping: false,
    typingUserName: "",
    typingTimer: null,
    userId: null,
    groupMembers: [],
    loadingMembers: false,
    editingMsg: null,
    deleteDialog: false,
    deleteLoading: false,
    msgToDelete: null,
    mediaViewerOpen: false,
    mediaViewerItems: [],
    mediaViewerIndex: 0,
  }),
  computed: {
    messageInputDisabled() {
      if (this.editingMsg) return false;
      return this.selectingFile || !!this.pendingFile || this.sending;
    },
    composerPlaceholder() {
      if (this.selectingFile) return "Selecting attachment…";
      if (this.pendingFile) return "Attachment ready — press send";
      return "Type a message…";
    },
  },
  mounted() {
    try {
      this.userId = JSON.parse(localStorage.getItem("user"))?.id;
    } catch (e) {
      console.error(e);
    }
    this.fetchConvs();
  },
  beforeDestroy() {
    this.endFilePickerSession();
    this.socket?.disconnect();
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    ...mapMutations("society", ["setUnreadChatCount"]),

    getApiErrorMessage(err, fallback) {
      const data = err?.response?.data;
      if (data) {
        if (typeof data.message === "string" && data.message.trim()) {
          return data.message.trim();
        }
        if (typeof data.error === "string" && data.error.trim()) {
          return data.error.trim();
        }
      }
      return fallback;
    },

    groupIcon(t) {
      return (
        {
          society: "mdi-home-city-outline",
          block: "mdi-domain",
          all_shop: "mdi-store-outline",
          committee: "mdi-account-star-outline",
        }[t] || "mdi-chat-outline"
      );
    },
    groupColor(t) {
      const primary = this.$store.state.theme.primaryColor || "#6366f1";
      return (
        {
          society: primary,
          block: primary,
          all_shop: primary,
          committee: primary,
        }[t] || primary
      );
    },
    groupLabel(t) {
      return (
        {
          society: "All Society Members",
          block: "Block Members",
          all_shop: "Shop Owners",
          committee: "Committee Members",
        }[t] || "Group"
      );
    },
    getInitials(name) {
      if (!name) return "M";
      const words = name.trim().split(/\s+/);
      if (words.length >= 2) {
        return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      }
      return name.charAt(0).toUpperCase();
    },
    formatMemberRole(m) {
      if (m.type === "admin") return "Society Admin";
      const isShop =
        m.unit_type == 2 || m.unit_type === "shop";
      const role = isShop ? "Shop" : "Resident";
      const convType = this.selected?.type;
      const hn = m.house_number ? String(m.house_number).trim() : "";

      if (convType === "all_shop" || isShop) {
        return hn ? `${role} (${hn})` : role;
      }
      if (!hn && !m.flat_name) return role;
      if (hn && m.flat_name) {
        const prefix = `${m.flat_name}-`;
        if (hn.startsWith(prefix) || hn.startsWith(`${m.flat_name} -`)) {
          return `${role} (${hn})`;
        }
        return `${role} (${m.flat_name} - ${hn})`;
      }
      return `${role} (${hn || m.flat_name})`;
    },
    isMine(m) {
      return m.sender_type === "admin" && m.sender_id === this.userId;
    },
    formatTime(ts) {
      return ts
        ? new Date(ts).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "";
    },

    initSocket() {
      this.socket?.disconnect();
      let token = "";
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        token = user?.token || "";
      } catch (e) {
        console.error("Error getting token:", e);
      }

      this.socket = io(process.env.VUE_APP_API_URL || "http://localhost:3333", {
        auth: { token },
        query: { token },
      });
      this.socket.on("connect", () => {
        this.isConnected = true;
        if (this.selected)
          this.socket.emit("join_conversation", {
            conversationId: this.selected.id,
          });
      });
      this.socket.on("disconnect", () => {
        this.isConnected = false;
      });
      this.socket.on("message:new", (msg) => {
        const conv = this.conversations.find(
          (c) => c.id === msg.conversation_id,
        );
        if (conv) {
          this.$set(conv, "last_message", msg);
        } else {
          this.fetchConvs();
        }

        if (this.selected && msg.conversation_id === this.selected.id) {
          this.messages.push(msg);
          this.scrollBottom();
          this.socket.emit("read_receipt", {
            conversationId: this.selected.id,
            messageId: msg.id,
          });
        } else {
          // Increment unread count for the specific conversation and global badge
          if (conv && msg.sender_type !== "admin") {
            conv.unread_count = (conv.unread_count || 0) + 1;
            this.setUnreadChatCount(
              this.$store.state.society.unreadChatCount + 1,
            );
          }
        }
      });
      this.socket.on("message:edit", (msg) => {
        if (this.selected && msg.conversation_id === this.selected.id) {
          const index = this.messages.findIndex((m) => m.id === msg.id);
          if (index !== -1) {
            this.$set(this.messages, index, msg);
          }
        }
      });
      this.socket.on("message:delete", (msg) => {
        if (this.selected && msg.conversation_id === this.selected.id) {
          const index = this.messages.findIndex((m) => m.id === msg.id);
          if (index !== -1) {
            this.$set(this.messages, index, msg);
          }
        }
      });
      this.socket.on("typing", (d) => {
        if (
          this.selected &&
          d.conversationId === this.selected.id &&
          d.userId !== this.userId
        ) {
          this.isTyping = d.isTyping;
          this.typingUserName = d.userName || "Someone";
          if (this.isTyping) {
            this.scrollBottom();
          }
        }
      });
    },

    async fetchConvs() {
      this.loadingConvs = true;
      try {
        const r = await this.$axios.get("/societyadmin/chat/conversations");
        this.conversations = r.data.data || [];
      } catch (e) {
        this.conversations = [
          { id: 1, name: "Society Group", type: "society" },
          { id: 2, name: "A Block", type: "block" },
          { id: 3, name: "Committee Group", type: "committee" },
        ];
      } finally {
        this.loadingConvs = false;
      }
    },
    async syncGroups() {
      if (this.syncing) return;
      this.syncing = true;
      try {
        await this.$axios.post("/societyadmin/chat/sync");
        this.setAlert({
          showAlert: true,
          variant: "success",
          timeout: "3000",
          message: "Groups synced successfully",
        });
        await this.fetchConvs();
      } catch (e) {
        this.setAlert({
          showAlert: true,
          variant: "red accent-3",
          timeout: "3000",
          message: "Failed to sync groups",
        });
      } finally {
        this.syncing = false;
      }
    },

    async selectConv(c) {
      if (this.selected && this.socket)
        this.socket.emit("leave_conversation", {
          conversationId: this.selected.id,
        });
      this.selected = c;
      this.drawerOpen = false;
      this.groupMembers = [];
      if (!this.socket) this.initSocket();

      // Sync group membership first (ensures members with multi-block units
      // are correctly placed), then fetch messages + members in parallel.
      try { await this.$axios.post("/societyadmin/chat/sync"); } catch (e) { console.warn("Chat sync:", e?.message); }

      await Promise.all([this.fetchMsgs(c.id), this.fetchMembers(c.id)]);

      // Mark as read if there are messages
      if (this.messages.length > 0 && c.unread_count > 0) {
        const lastMsg = this.messages[this.messages.length - 1];
        try {
          await this.$axios.post(
            `/societyadmin/chat/conversations/${c.id}/read`,
            {
              message_id: lastMsg.id,
            },
          );
          // Update global unread count before resetting local
          const newTotal = Math.max(
            0,
            this.$store.state.society.unreadChatCount - c.unread_count,
          );
          this.setUnreadChatCount(newTotal);
          // Update local unread count
          c.unread_count = 0;
        } catch (e) {
          console.error("MarkRead Error:", e);
        }
      }
    },

    async fetchMsgs(id) {
      this.loadingMsgs = true;
      this.messages = [];
      try {
        const r = await this.$axios.get(
          `/societyadmin/chat/conversations/${id}/messages`,
        );
        const d = r.data.data;
        this.messages = (d.data || d || []).slice().reverse();
        this.scrollBottom();
        setTimeout(() => {
          this.scrollBottom();
        }, 500);
      } catch (e) {
        this.messages = [
          {
            id: 1,
            content: `Welcome to ${this.selected.name}`,
            message_type: "text",
            sender_type: "system",
            sender_id: 0,
            created_at: new Date(),
          },
        ];
      } finally {
        this.loadingMsgs = false;
      }
    },

    async fetchMembers(id) {
      this.loadingMembers = true;
      try {
        const r = await this.$axios.get(
          `/societyadmin/chat/conversations/${id}/members`,
        );
        this.groupMembers = r.data.data || [];
      } catch (e) {
        console.error("FetchMembers Error:", e);
      } finally {
        this.loadingMembers = false;
      }
    },

    openFilePicker() {
      if (this.editingMsg || this.selectingFile) return;
      this.endFilePickerSession();
      this.selectingFile = true;
      this._filePickerFocusHandler = () => {
        setTimeout(() => {
          this.selectingFile = false;
          this.endFilePickerSession();
        }, 300);
      };
      window.addEventListener("focus", this._filePickerFocusHandler);
      this.$refs.fi?.click();
    },

    endFilePickerSession() {
      if (this._filePickerFocusHandler) {
        window.removeEventListener("focus", this._filePickerFocusHandler);
        this._filePickerFocusHandler = null;
      }
    },

    clearPendingFile() {
      this.pendingFile = null;
      if (this.$refs.fi) this.$refs.fi.value = "";
    },

    resetFileInput() {
      if (this.$refs.fi) this.$refs.fi.value = "";
      this.selectingFile = false;
      this.endFilePickerSession();
    },

    onFile(e) {
      const f = e.target.files[0];
      this.selectingFile = false;
      this.endFilePickerSession();
      if (!f) return;

      if (isHarmfulChatFileName(f.name)) {
        this.setAlert({
          showAlert: true,
          variant: "red accent-3",
          timeout: "3000",
          message: `Harmful file type detected. The following extensions are blocked: ${HARMFUL_CHAT_EXTENSIONS.join(", ")}`,
        });
        this.resetFileInput();
        return;
      }
      this.draft = "";
      this.stopTyping();
      this.pendingFile = f;
    },

    stopTyping() {
      clearTimeout(this.typingTimer);
      if (this.amITyping && this.socket && this.selected) {
        this.amITyping = false;
        this.socket.emit("typing", {
          conversationId: this.selected.id,
          isTyping: false,
        });
      }
    },

    onTyping() {
      if (this.messageInputDisabled) return;
      if (!this.socket || !this.selected) return;

      // Only send typing: true if we weren't already typing
      if (!this.amITyping) {
        this.amITyping = true;
        this.socket.emit("typing", {
          conversationId: this.selected.id,
          isTyping: true,
        });
      }

      clearTimeout(this.typingTimer);
      this.typingTimer = setTimeout(() => {
        this.amITyping = false;
        this.socket?.emit("typing", {
          conversationId: this.selected.id,
          isTyping: false,
        });
      }, 2000);
    },

    startEdit(m) {
      this.editingMsg = m;
      this.draft = m.content;
      this.clearPendingFile();
      this.selectingFile = false;
      this.endFilePickerSession();
    },
    cancelEdit() {
      this.editingMsg = null;
      this.draft = "";
    },

    async deleteMsg(m) {
      this.msgToDelete = m;
      this.deleteDialog = true;
    },

    async confirmDeleteMsg() {
      if (!this.msgToDelete) return;
      this.deleteLoading = true;
      try {
        await this.$axios.delete(
          `/societyadmin/chat/conversations/${this.selected.id}/messages/${this.msgToDelete.id}`,
        );
        this.deleteDialog = false;
        this.msgToDelete = null;
      } catch (e) {
        this.setAlert({
          showAlert: true,
          variant: "red accent-3",
          timeout: "3000",
          message: this.getApiErrorMessage(e, "Failed to delete message"),
        });
      } finally {
        this.deleteLoading = false;
      }
    },

    async send() {
      if ((!this.draft.trim() && !this.pendingFile) || this.sending) return;
      this.sending = true;
      let type = "text",
        content = this.draft.trim();
      try {
        if (this.pendingFile) {
          const f = this.pendingFile;
          if (f.type.startsWith("image/")) type = "image";
          else if (f.type.startsWith("video/")) type = "video";
          else if (f.type.startsWith("audio/")) type = "audio";
          else type = "file";
          const fd = new FormData();
          fd.append("media", f); // Correctly append the file
          const uploadRes = await this.$axios.post(
            "/societyadmin/chat/media/upload",
            fd,
          );
          if (!uploadRes.data?.status) {
            const err = new Error(
              uploadRes.data?.message || "Failed to upload attachment",
            );
            err.response = { data: uploadRes.data };
            throw err;
          }
          content = uploadRes.data.data.url; // Use the actual server URL
        }
        if (this.editingMsg) {
          // Edit existing message
          const r = await this.$axios.put(
            `/societyadmin/chat/conversations/${this.selected.id}/messages/${this.editingMsg.id}`,
            { content },
          );

          const index = this.messages.findIndex((m) => m.id === r.data.data.id);
          if (index !== -1) {
            this.$set(this.messages, index, r.data.data);
          }
          this.cancelEdit();
        } else {
          // Send new message
          const r = await this.$axios.post(
            `/societyadmin/chat/conversations/${this.selected.id}/messages`,
            { message_type: type, content },
          );

          // Push to UI immediately for better UX
          const newMessage = r.data.data;
          if (
            newMessage &&
            !this.messages.find((m) => m.id === newMessage.id)
          ) {
            this.messages.push(newMessage);
            this.scrollBottom();
          }
          this.draft = "";
          this.clearPendingFile();
        }
      } catch (e) {
        this.setAlert({
          showAlert: true,
          variant: "red accent-3",
          timeout: "5000",
          message: this.getApiErrorMessage(e, "Failed to send message"),
        });
      } finally {
        this.sending = false;
      }
    },

    resolveMedia(path) {
      if (!path) return "";
      if (path.startsWith("http")) return path;
      // Use /\/api$/ (end-of-string anchor) to avoid stripping "api" from
      // the subdomain portion of the URL (e.g. "//api.ismartsociety.com")
      const base = this.$axios.defaults.baseURL.replace(/\/api$/, "");
      const fullPath = path.startsWith("/")
        ? path
        : `/uploads/chat/media/${path}`;
      return `${base}${fullPath}`;
    },
    openUrl(url) {
      window.open(this.resolveMedia(url), "_blank");
    },
    isPdf(content) {
      if (!content) return false;
      return content.toLowerCase().endsWith(".pdf");
    },
    openLightbox(type, src) {
      // Map internal type names to MediaViewer types
      const mvType = type === "pdf" ? "document" : type; // 'image' | 'video' | 'document'
      const name = src.split("/").pop() || "Media";
      this.mediaViewerItems = [{ url: src, type: mvType, name }];
      this.mediaViewerIndex = 0;
      this.mediaViewerOpen = true;
    },

    scrollBottom() {
      this.$nextTick(() => {
        const b = this.$refs.msgBox;
        if (b) b.scrollTop = b.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
/* ── Root ── */
.chat-root {
  --primary: #f87676;
  --btn: #30384d;
  display: flex;
  height: calc(100vh - 84px);
  background: #f0f4f8;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
  font-family: "Inter", "Segoe UI", sans-serif;
}

/* ── Sidebar ── */
.chat-sidebar {
  width: 310px;
  min-width: 310px;
  background: #ffffff;
  border-right: 1px solid #eaecf0;
  display: flex;
  flex-direction: column;
}
.sidebar-header {
  padding: 18px 16px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eaecf0;
  background: #fff;
}
.sidebar-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a202c;
  letter-spacing: -0.3px;
}
.sh-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.sync-btn {
  width: 32px;
  height: 32px;
  padding: 0 !important;
}
.conn-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.conn-dot.green {
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.2);
}
.conn-dot.red {
  background: #ef4444;
}

.sidebar-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 6px;
}
.sidebar-list::-webkit-scrollbar {
  width: 4px;
}
.sidebar-list::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 4px;
}

.conv-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s;
  margin-bottom: 2px;
}
.conv-item:hover {
  background: #f7f8fc;
}
.conv-item.active {
  background: color-mix(in srgb, var(--primary) 12%, #fff);
  border-left: 3px solid var(--primary);
  padding-left: 9px;
}
.conv-item.unread .conv-name {
  font-weight: 800 !important;
  color: #000 !important;
}
.conv-item.unread .conv-last {
  font-weight: 600 !important;
  color: #1a202c !important;
}
.conv-avatar {
  width: 46px;
  height: 46px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.conv-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.cm-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.conv-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.unread-badge {
  background: var(--primary);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
  min-width: 20px;
  text-align: center;
}
.conv-last {
  font-size: 12px;
  color: #718096;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
}
.conv-sub {
  font-size: 11px;
  color: #a0aec0;
}

/* ── Main ── */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}
.no-conv {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  gap: 14px;
  background: linear-gradient(135deg, #fafbfc 0%, #f0f4f8 100%);
}
.no-conv p {
  font-size: 15px;
  font-weight: 500;
}

/* ── Chat Header ── */
.chat-header {
  padding: 14px 20px;
  background: #fff;
  border-bottom: 1px solid #eaecf0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.ch-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ch-avatar {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ch-name {
  font-size: 16px;
  font-weight: 700;
  color: #1a202c;
}
.ch-sub {
  font-size: 11px;
  color: var(--primary);
  cursor: pointer;
  font-weight: 500;
  margin-top: 2px;
}
.ch-sub:hover {
  text-decoration: underline;
}
.status-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}
.status-pill.online {
  background: #dcfce7;
  color: #15803d;
}
.status-pill.offline {
  background: #fee2e2;
  color: #dc2626;
}

/* ── Messages ── */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='20' cy='20' r='1' fill='%23e2e8f0'/%3E%3C/svg%3E")
    #f8fafc;
}
.chat-messages::-webkit-scrollbar {
  width: 5px;
}
.chat-messages::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 5px;
}

.msg-wrap {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.msg-wrap.mine {
  justify-content: flex-end;
}

.msg-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--primary) 15%, #fff);
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: var(--primary);
}
.msg-bubble {
  max-width: 62%;
  background: #ffffff;
  border-radius: 18px 18px 18px 4px;
  padding: 10px 14px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.07);
}
.msg-bubble.mine {
  background: var(--primary);
  color: #fff;
  border-radius: 18px 18px 4px 18px;
  box-shadow: 0 2px 10px color-mix(in srgb, var(--primary) 35%, transparent);
}
.msg-sender {
  font-size: 11px;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 4px;
}
.msg-bubble.mine .msg-sender {
  color: rgba(255, 255, 255, 0.75);
}
.msg-text {
  font-size: 14px;
  line-height: 1.55;
  word-break: break-word;
}
.msg-time {
  font-size: 10px;
  opacity: 0.55;
  text-align: right;
  margin-top: 5px;
}

.media-img {
  max-width: 220px;
  max-height: 220px;
  border-radius: 12px;
  cursor: pointer;
  display: block;
  width: auto;
  height: auto;
}
.media-video {
  max-width: 260px;
  max-height: 260px;
  border-radius: 12px;
  width: auto;
  height: auto;
}
.media-audio {
  max-width: 240px;
}
.file-attachment {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  padding: 8px 12px;
  text-decoration: none;
  color: inherit;
  font-size: 13px;
  font-weight: 500;
}
.pdf-clickable {
  cursor: pointer;
}
.pdf-clickable:hover {
  background: rgba(0, 0, 0, 0.12);
}
.msg-bubble.mine .file-attachment {
  background: rgba(255, 255, 255, 0.2);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .chat-root {
    flex-direction: column;
    height: calc(100vh - 56px);
    border-radius: 0;
  }
  .chat-sidebar {
    width: 100%;
    min-width: unset;
    height: 180px;
    min-height: 180px;
    border-right: none;
    border-bottom: 1px solid #eaecf0;
  }
  .sidebar-list {
    display: flex;
    flex-direction: row;
    gap: 6px;
    padding: 6px 8px;
    overflow-x: auto;
    overflow-y: hidden;
  }
  .conv-item {
    flex-direction: column;
    align-items: center;
    padding: 8px;
    min-width: 70px;
    border-radius: 10px;
  }
  .conv-meta {
    display: none;
  }
  .conv-item.active {
    border-left: none;
    border-bottom: 3px solid var(--primary);
    padding-left: 8px;
  }
}
@media (max-width: 480px) {
  .msg-bubble {
    max-width: 82%;
  }
  .chat-messages {
    padding: 12px 10px;
  }
  .composer {
    padding: 8px 10px;
  }
}

/* Typing */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 14px;
}
.typing-indicator span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #94a3b8;
  animation: bounce 0.9s infinite;
}
.typing-indicator span:nth-child(2) {
  animation-delay: 0.15s;
}
.typing-indicator span:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-6px);
  }
}

/* ── Composer ── */
.composer {
  background: #fff;
  border-top: 1px solid #eaecf0;
  padding: 12px 16px;
  box-shadow: 0 -1px 8px rgba(0, 0, 0, 0.04);
}
.file-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  background: color-mix(in srgb, var(--primary) 8%, #fff);
  border: 1px solid color-mix(in srgb, var(--primary) 25%, transparent);
  border-radius: 10px;
  padding: 7px 12px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #4a5568;
}
.rm-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #ef4444;
  font-size: 14px;
  margin-left: auto;
}
.composer-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}
.msg-input {
  flex: 1;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 14px;
  font-size: 14px;
  resize: none;
  outline: none;
  max-height: 120px;
  background: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s;
  font-family: inherit;
}
.msg-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 15%, transparent);
  background: #fff;
}
.msg-input:disabled,
.msg-input--disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
  border-color: #e2e8f0;
  box-shadow: none;
}
.msg-input:disabled:focus {
  border-color: #e2e8f0;
  box-shadow: none;
  background: #f1f5f9;
}
.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 8px;
  border-radius: 10px;
  transition: background 0.15s, color 0.15s;
}
.icon-btn:hover {
  background: #f0f4f8;
  color: var(--primary);
}
.send-btn {
  width: 44px;
  height: 44px;
  background: var(--primary);
  border: none;
  border-radius: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s, transform 0.1s;
  flex-shrink: 0;
  box-shadow: 0 3px 12px color-mix(in srgb, var(--primary) 40%, transparent);
}
.send-btn:hover:not(:disabled) {
  opacity: 0.88;
  transform: scale(1.04);
}
.send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

/* ── Drawer ── */
.drawer-overlay {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.2);
  z-index: 50;
  display: flex;
  justify-content: flex-end;
  backdrop-filter: blur(2px);
}
.member-drawer {
  width: 288px;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  animation: slideIn 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
}
@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}
.drawer-header {
  padding: 18px 18px 16px;
  border-bottom: 1px solid #eaecf0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  font-size: 15px;
  color: #1a202c;
}
.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}
.member-list {
  display: flex;
  flex-direction: column;
}
.member-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid #f7f8fc;
  transition: background 0.12s;
}
.member-item:hover {
  background: #f7f8fc;
}
.member-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.member-avatar.admin {
  background: var(--primary);
}
.member-avatar.resident {
  background: color-mix(in srgb, var(--primary) 18%, #fff);
}
.member-avatar.resident .avatar-initial {
  color: var(--primary);
}
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}
.avatar-initial {
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}
.member-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
}
.member-role {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
  text-transform: capitalize;
}

/* Misc */
.loading-state,
.empty-state {
  padding: 28px;
  text-align: center;
  color: #a0aec0;
  font-size: 14px;
}
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.hidden {
  display: none;
}
.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.2s;
}
.drawer-enter,
.drawer-leave-to {
  opacity: 0;
}
</style>
